import { motion } from "framer-motion";
import { Truck, DollarSign, TrendingDown, ArrowDown } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Math.abs(value))
    .replace("ZAR", "R");
};

// Color helper: negative = red, positive = blue, zero = black
const getValueColor = (value) => {
  if (value > 0) return "#0891B2"; // Blue/Teal - positive
  if (value < 0) return "#DC2626"; // Red - negative
  return "#0F172A"; // Black - neutral/zero
};

export default function KPICards({ stats, settings }) {
  // Get base values from turnover engine
  const data = stats?.turnover_engine;
  if (!data) return null;

  // Recalculate using same logic as TurnoverTargetEngine
  const fleetTarget = data.fleet_target || 0;
  const officeCost = settings?.office_cost || data.office_cost || 216000;
  const margin = (settings?.margin || 33.3) / 100;

  // Fleet calculations
  const fleetSurplus = data.fleet_actual - fleetTarget;
  
  // Adjusted office cost (same as in TurnoverTargetEngine)
  const adjustedOfficeCost = officeCost - fleetSurplus;
  
  // Required 3rd party and Gap (same formula as TurnoverTargetEngine)
  const required3rdParty = adjustedOfficeCost > 0 ? adjustedOfficeCost / margin : 0;
  const gap = required3rdParty + data.fleet_actual - data.actual_3rd_party;

  const cards = [
    {
      title: "Fleet Recovery",
      value: formatCurrency(fleetSurplus),
      prefix: fleetSurplus < 0 ? "-" : "",
      icon: Truck,
      color: getValueColor(fleetSurplus),
      showArrow: fleetSurplus < 0,
      testId: "kpi-fleet-recovery",
    },
    {
      title: "Remaining Overhead Cost",
      value: formatCurrency(adjustedOfficeCost),
      icon: DollarSign,
      color: "#0F172A", // Always black as it's a cost to cover
      testId: "kpi-remaining-overhead",
    },
    {
      title: "Remaining 3rd Party Turnover",
      value: formatCurrency(gap),
      icon: TrendingDown,
      color: getValueColor(-gap), // Positive gap means still need money (red)
      testId: "kpi-remaining-3rd-party",
    },
  ];

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-4"
      data-testid="kpi-cards"
    >
      {cards.map((card, index) => (
        <motion.div
          key={card.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.4 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm"
          data-testid={card.testId}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
              <card.icon className="w-5 h-5 text-[#64748B]" />
            </div>
            <span className="text-[#64748B] text-sm font-medium">{card.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-3xl font-bold font-mono"
              style={{ color: card.color }}
            >
              {card.prefix}{card.value}
            </span>
            {card.showArrow && (
              <ArrowDown className="w-5 h-5 text-[#DC2626]" />
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}
